// The site's own chat bubble (chat.js on the server). Messages land in the admin Inbox
// with a ding and a Discord ping; replies from the Inbox show up here within seconds while
// the visitor is on the site, and also arrive by email. The browser keeps the conversation's
// private key, so only this visitor can read it. Hidden on the admin page, in the demo and
// on the distraction-free Live session.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from './api';
import { useI18n } from './i18n';

const HIDDEN_PATHS = ['/admin', '/app/session'];
const STORE = 'mm-chat';
const OPEN_POLL_MS = 8000;
const CLOSED_POLL_MS = 60000;

function readStore() {
  try {
    return JSON.parse(localStorage.getItem(STORE)) || null;
  } catch {
    return null;
  }
}
function writeStore(value) {
  try {
    if (value) localStorage.setItem(STORE, JSON.stringify(value));
    else localStorage.removeItem(STORE);
  } catch {
    // Without storage the chat still works until the page is closed.
  }
}

const timeOf = (at, locale) => new Date(at).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8A2.5 2.5 0 0 1 17.5 16H10l-4.2 3.6c-.5.4-1.3 0-1.3-.6V16A2.5 2.5 0 0 1 4 13.5z" fill="currentColor" />
    </svg>
  );
}

// While the cookie banner is showing, the bubble sits just above it if they'd overlap
// (on phones the banner spans the whole width); it drops back once the visitor answers.
function useCookieBannerLift(hidden) {
  const [lift, setLift] = useState(0);
  useEffect(() => {
    if (hidden) return undefined;
    const measure = () => {
      const banner = document.querySelector('.cookie-banner');
      if (!banner) { setLift(0); return; }
      const b = banner.getBoundingClientRect();
      const reachesRight = b.right > window.innerWidth - 90;
      setLift(b.height && reachesRight ? Math.round(window.innerHeight - b.top + 12) : 0);
    };
    measure();
    const later = setTimeout(measure, 600);
    const onConsent = () => setTimeout(measure, 50);
    window.addEventListener('resize', measure);
    window.addEventListener('mm-consent', onConsent);
    return () => { clearTimeout(later); window.removeEventListener('resize', measure); window.removeEventListener('mm-consent', onConsent); };
  }, [hidden]);
  return lift;
}

function ChatBubble({ user }) {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  const member = user && !user.isDemo ? user : null;
  const hidden = Boolean(user && user.isDemo) || HIDDEN_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(readStore);
  const [chat, setChat] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', message: '', website: '' });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);
  const lift = useCookieBannerLift(hidden);

  const ours = chat ? chat.messages.filter((m) => m.from === 'us').length : 0;
  const unread = saved && chat ? Math.max(0, ours - (saved.seen || 0)) : 0;

  const load = useCallback(async () => {
    if (!saved) return;
    try {
      setChat(await api(`/chat/${saved.id}`, { headers: { 'X-Chat-Key': saved.token } }));
    } catch (err) {
      // The conversation was deleted from the Inbox: start fresh next time.
      if (err.status === 404) { writeStore(null); setSaved(null); setChat(null); }
    }
  }, [saved]);

  // Checks for replies: often while the chat is open, now and then while it's closed.
  useEffect(() => {
    if (!saved || hidden) return undefined;
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, open ? OPEN_POLL_MS : CLOSED_POLL_MS);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [saved, open, hidden, load]);

  // Reading the chat marks the replies as seen.
  useEffect(() => {
    if (!open || !saved || !chat || (saved.seen || 0) === ours) return;
    const next = { ...saved, seen: ours };
    writeStore(next);
    setSaved(next);
  }, [open, chat, ours, saved]);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, chat]);

  // Other parts of the site (like Home's "Chat with us") can open it.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('mm-open-chat', onOpen);
    return () => window.removeEventListener('mm-open-chat', onOpen);
  }, []);

  // Logging out on a shared computer starts a fresh conversation.
  const memberEmail = member ? member.email : null;
  const lastMember = useRef(memberEmail);
  useEffect(() => {
    if (lastMember.current && !memberEmail) { writeStore(null); setSaved(null); setChat(null); }
    lastMember.current = memberEmail;
  }, [memberEmail]);

  async function start(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api('/chat/start', { method: 'POST', body: { ...form, lang } });
      if (r.token) {
        const next = { id: r.id, token: r.token, seen: 0 };
        writeStore(next);
        setSaved(next);
        setChat(r);
      }
      setForm((f) => ({ ...f, message: '' }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function send(e) {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy(true);
    setError('');
    try {
      setChat(await api(`/chat/${saved.id}/messages`, { method: 'POST', body: { text: draft }, headers: { 'X-Chat-Key': saved.token } }));
      setDraft('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const onEnter = (submit) => (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(e); }
  };

  if (hidden) return null;
  const locale = lang === 'es' ? 'es-MX' : 'en-US';
  const replyEmail = member ? member.email : form.email;

  return (
    <div className="mmchat" style={lift ? { bottom: lift } : undefined}>
      {open && (
        <section className="mmchat-panel" role="dialog" aria-label={t('Chat with Market Matrix')}>
          <header className="mmchat-head">
            <div>
              <strong>Market Matrix</strong>
              <span>{t('We usually reply within a few hours. Replies show up here and in your email.')}</span>
            </div>
            <button type="button" className="mmchat-close" onClick={() => setOpen(false)} aria-label={t('Close chat')}>&times;</button>
          </header>

          <div className="mmchat-list" ref={listRef}>
            <div className="mmchat-msg us">
              <p>{member ? t('Hi {name}! 👋 Questions, problems or ideas? Write us here.', { name: member.name.split(' ')[0] }) : t('Hi! 👋 Questions, problems or ideas? Write us here.')}</p>
            </div>
            {chat && chat.messages.map((m, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className={`mmchat-msg ${m.from}`}>
                <p>{m.text}</p>
                <span className="mmchat-time">{m.from === 'us' ? 'Lou · ' : ''}{timeOf(m.at, locale)}</span>
              </div>
            ))}
            {chat && ours === 0 && (
              <p className="mmchat-note">{t("Thanks! We've got your message. We'll reply here, and by email in case you've left the site.")}</p>
            )}
          </div>

          {error && <p className="mmchat-error" role="alert">{error}</p>}

          {saved && chat ? (
            <form className="mmchat-compose" onSubmit={send}>
              <textarea rows="2" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onEnter(send)}
                placeholder={t('Write a message…')} aria-label={t('Write a message…')} maxLength={5000} />
              <button type="submit" disabled={busy || !draft.trim()}>{t('Send')}</button>
            </form>
          ) : (
            <form className="mmchat-start" onSubmit={start}>
              {!member && (
                <>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('Your name')} aria-label={t('Your name')} required maxLength={120} autoComplete="name" />
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('Your email (for our reply)')} aria-label={t('Your email (for our reply)')} required maxLength={200} autoComplete="email" />
                </>
              )}
              <input className="mmchat-hp" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} aria-hidden="true" />
              <textarea rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} onKeyDown={onEnter(start)}
                placeholder={t('Write a message…')} aria-label={t('Write a message…')} required maxLength={5000} />
              <button type="submit" disabled={busy || !form.message.trim()}>{busy ? t('Sending…') : t('Send')}</button>
              {replyEmail && <span className="mmchat-small">{t('We reply here and to {email}.', { email: replyEmail })}</span>}
            </form>
          )}
        </section>
      )}

      <button type="button" className="mmchat-button" onClick={() => setOpen((o) => !o)}
        aria-label={open ? t('Close chat') : t('Chat with Market Matrix')} aria-expanded={open}>
        {open ? <span className="mmchat-x" aria-hidden="true">&times;</span> : <ChatIcon />}
        {!open && unread > 0 && <span className="mmchat-dot" aria-label={t('New reply')}>{unread}</span>}
      </button>
    </div>
  );
}

export default ChatBubble;
